import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, Receipt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RefundOrderList } from "@/components/RefundOrderList";

const REASONS = [
  ["change_of_mind", "Change of mind"],
  ["failed_product", "Failed, incomplete or faulty product"],
  ["duplicate", "Accidental duplicate payment"],
  ["other", "Other"],
];

export default function RefundRequest() {
  const [data, setData] = useState(null);
  const [order, setOrder] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    api.get("/refunds/orders").then((r) => setData(r.data)).catch(() => setData({ username: null, email: "", orders: [], failed: true }));
  }, []);
  const chosen = data?.orders.find((o) => o.order_number === order);

  const submit = async () => {
    setSending(true);
    try {
      const r = await api.post("/refunds/request", { order_number: order, reason, note: note.trim() || null });
      setSent(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "We couldn't send your request. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (!data) return <p className="p-10 text-center text-[#8A7F76]">Loading…</p>;

  return (
    <div data-testid="refund-request-page" className="mx-auto max-w-2xl px-5 py-10 sm:px-6">
      <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#A84A32]"><Receipt size={16} weight="duotone" /> Refund request</p>
      <h1 className="mt-2 font-serif text-4xl text-[#2D2824]">Request a refund</h1>

      {sent ? (
        <div data-testid="refund-request-sent" className="mt-8 rounded-2xl bg-[#EBF4F5] p-6">
          <CheckCircle size={36} weight="duotone" className="text-[#36858B]" />
          <p className="mt-3 font-serif text-2xl text-[#2D2824]">Request received</p>
          <p className="mt-2 text-sm leading-relaxed text-[#4A4540]">
            Your reference is <strong data-testid="refund-request-reference">{sent.reference}</strong> for order {sent.order_number}.
            We've emailed you a confirmation and will review it against our Refund Policy and reply by email.
          </p>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          <div data-testid="refund-request-account" className="rounded-xl bg-white p-4 text-sm text-[#4A4540] ring-1 ring-[#EBE4DA]">
            <p><span className="text-[#8A7F76]">Username:</span> <strong>{data.username || "—"}</strong></p>
            <p className="mt-1"><span className="text-[#8A7F76]">Email:</span> {data.email}</p>
          </div>

          <RefundOrderList orders={data.orders} value={order} onChange={setOrder} />

          {chosen && (
            <div className="space-y-4">
              {chosen.used && (
                <p data-testid="refund-request-used-note" className="rounded-lg bg-[#FDF7EB] p-3 text-sm leading-relaxed text-[#6E665E]">
                  This item has already been created or used, so a change of mind refund may not apply. Refunds are still available if it failed, is incomplete or has a serious problem.
                </p>
              )}
              <div>
                <p className="mb-2 text-sm font-medium text-[#2D2824]">Reason</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {REASONS.map(([k, label]) => (
                    <button key={k} type="button" data-testid={`refund-reason-${k}`} onClick={() => setReason(k)}
                      className={`rounded-xl border-2 px-4 py-3 text-left text-sm transition-colors ${reason === k ? "border-[#E06D53] bg-[#FFF4F0] text-[#2D2824]" : "border-[#EBE4DA] bg-white text-[#4A4540] hover:border-[#F7C4B8]"}`}>
                      {label}
                    </button>
                  ))}
                </div>
                {reason === "failed_product" && (
                  <p className="mt-2 text-xs leading-relaxed text-[#6E665E]">Tip: you can try creating it again from Keepsakes or the memory itself at no extra cost. If it still doesn't work, send this request.</p>
                )}
              </div>
              <div>
                <label htmlFor="refund-note" className="mb-2 block text-sm font-medium text-[#2D2824]">Anything else we should know? (optional)</label>
                <textarea id="refund-note" data-testid="refund-request-note" value={note} maxLength={1000} rows={3} onChange={(e) => setNote(e.target.value)}
                  className="w-full rounded-xl border border-[#EBE4DA] bg-white p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#36858B]/40" />
                <p className="mt-1 text-xs text-[#8A7F76]">Please don't include your password or card details.</p>
              </div>
              <Button data-testid="refund-request-submit" disabled={!reason || sending} onClick={submit}
                className="rounded-full bg-[#E06D53] px-7 py-5 text-sm font-semibold text-white hover:bg-[#C85B43]">
                {sending ? "Sending…" : "Send refund request"}
              </Button>
            </div>
          )}
          <p className="text-xs leading-relaxed text-[#8A7F76]">
            Read our <a href="/refunds" className="text-[#36858B] underline">Refund Policy</a>. This does not limit your rights under the Australian Consumer Law.
          </p>
        </div>
      )}
    </div>
  );
}
